// Page objects belong to the application, not to a testing area, so they are
// navigated to by their own slug rather than through APPLICATION_MODULES.
export const APPLICATION_POMS_SLUG = 'page-objects';

export const APPLICATION_MODULES = {
  brd: {
    id: 'brd',
    slug: 'brd',
    mode: 'pdf',
    intakeMode: 'document',
    label: 'BRD Flow',
    shortLabel: 'BRD',
    description: 'Requirements documents, sections, coverage, and automation.',
    theme: 'brd',
  },
  requirements: {
    id: 'requirements',
    slug: 'requirements',
    mode: 'requirements',
    intakeMode: 'text',
    label: 'Text Requirements',
    shortLabel: 'Text',
    description: 'Focused requirements and screenshots converted into test coverage.',
    theme: 'requirements',
  },
  api: {
    id: 'api',
    slug: 'api',
    mode: 'api-testing',
    intakeMode: 'api',
    label: 'API Testing',
    shortLabel: 'API',
    description: 'Swagger and OpenAPI contracts, operation coverage, and scripts.',
    theme: 'api',
  },
  excel: {
    id: 'excel',
    slug: 'excel',
    mode: 'excel-testcases',
    intakeMode: 'excel',
    label: 'Excel Test Cases',
    shortLabel: 'Excel',
    description: 'Mapped workbook cases continued into review and automation.',
    theme: 'excel',
  },
  feature: {
    id: 'feature',
    slug: 'features',
    mode: 'feature-capture',
    label: 'Test Feature',
    shortLabel: 'Features',
    description: 'Captured browser journeys, cases, page objects, scripts, and runs.',
    theme: 'capture',
  },
};

export const APPLICATION_MODULE_ORDER = ['brd', 'requirements', 'api', 'excel', 'feature'];

const MODULE_ALIASES = {
  pdf: 'brd',
  document: 'brd',
  text: 'requirements',
  'api-testing': 'api',
  openapi: 'api',
  'excel-testcases': 'excel',
  testcase_excel: 'excel',
  'feature-capture': 'feature',
  features: 'feature',
};

const decodePart = (value = '') => {
  try {
    return decodeURIComponent(String(value || ''));
  } catch (_err) {
    return String(value || '');
  }
};

export const normalizeApplicationModule = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  const resolved = MODULE_ALIASES[normalized] || normalized;
  return APPLICATION_MODULES[resolved] ? resolved : '';
};

export const applicationModuleFromMode = (mode = '') => normalizeApplicationModule(mode);

export const parseApplicationRoute = (pathname = '') => {
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean)
    .map(decodePart);

  if (parts[0] !== 'applications') {
    return {
      isApplicationRoute: false,
      kind: '',
      applicationId: '',
      module: '',
      resourceId: '',
      stage: '',
      tail: [],
    };
  }

  const applicationId = String(parts[1] || '').trim();
  if (!applicationId) {
    return {
      isApplicationRoute: true,
      kind: 'applications',
      applicationId: '',
      module: '',
      resourceId: '',
      stage: '',
      tail: [],
    };
  }

  if (parts[2] === APPLICATION_POMS_SLUG) {
    return {
      isApplicationRoute: true,
      kind: 'poms',
      applicationId,
      module: '',
      resourceId: '',
      stage: '',
      tail: parts.slice(3),
    };
  }

  const module = normalizeApplicationModule(parts[2]);
  if (!module) {
    return {
      isApplicationRoute: true,
      kind: 'overview',
      applicationId,
      module: '',
      resourceId: '',
      stage: '',
      tail: parts.slice(2),
    };
  }

  const tail = parts.slice(3);
  if (!tail.length) {
    return {
      isApplicationRoute: true,
      kind: 'module',
      applicationId,
      module,
      resourceId: '',
      stage: '',
      tail,
    };
  }

  if (tail[0] === 'new') {
    return {
      isApplicationRoute: true,
      kind: 'new',
      applicationId,
      module,
      resourceId: '',
      stage: 'new',
      tail,
    };
  }

  if (module === 'excel' && tail[0] === 'import' && tail[1]) {
    return {
      isApplicationRoute: true,
      kind: 'workflow',
      applicationId,
      module,
      resourceId: String(tail[1] || '').trim(),
      stage: 'mapping',
      tail,
    };
  }

  if (tail[0] === 'source' && tail[1]) {
    return {
      isApplicationRoute: true,
      kind: 'source',
      applicationId,
      module,
      resourceId: String(tail[1] || '').trim(),
      stage: 'overview',
      tail,
    };
  }

  const resourceId = String(tail[0] || '').trim();
  const stage = module === 'feature'
    ? String(tail[1] || 'capture').trim().toLowerCase()
    : String(tail[tail.length - 1] || 'source').trim().toLowerCase();
  return {
    isApplicationRoute: true,
    kind: 'workflow',
    applicationId,
    module,
    resourceId,
    stage,
    tail,
  };
};

export const buildApplicationsPath = (module = '') => {
  const normalizedModule = normalizeApplicationModule(module);
  return normalizedModule
    ? `/applications?module=${encodeURIComponent(normalizedModule)}`
    : '/applications';
};

export const buildApplicationPath = (applicationId = '') => (
  `/applications/${encodeURIComponent(String(applicationId || '').trim())}`
);

export const buildApplicationPomsPath = (applicationId = '') => (
  `${buildApplicationPath(applicationId)}/${APPLICATION_POMS_SLUG}`
);

export const buildApplicationPomEditorPath = (applicationId = '', pageName = '', mode = 'view') => {
  const normalizedMode = String(mode || '').trim().toLowerCase() === 'edit' ? 'edit' : 'view';
  return `${buildApplicationPomsPath(applicationId)}/${encodeURIComponent(
    String(pageName || '').trim()
  )}/${normalizedMode}`;
};

export const buildApplicationModulePath = (applicationId, module) => {
  const normalizedModule = normalizeApplicationModule(module);
  const config = APPLICATION_MODULES[normalizedModule];
  if (!config) return buildApplicationPath(applicationId);
  return `${buildApplicationPath(applicationId)}/${config.slug}`;
};

export const buildNewApplicationSourcePath = (applicationId, module) => (
  `${buildApplicationModulePath(applicationId, module)}/new`
);

export const buildApplicationSourcePath = (applicationId, module, sourceId) => (
  `${buildApplicationModulePath(applicationId, module)}/source/${encodeURIComponent(
    String(sourceId || '').trim()
  )}`
);

export const buildApplicationSourceOpenPath = (applicationId, module, sourceId) => {
  const normalizedModule = normalizeApplicationModule(module);
  if (!['brd', 'requirements'].includes(normalizedModule)) {
    return buildApplicationSourcePath(applicationId, module, sourceId);
  }
  const sourcePath = `${buildApplicationModulePath(applicationId, normalizedModule)}/${encodeURIComponent(String(sourceId || '').trim())}`;
  return `${sourcePath}?source=history`;
};

export const buildApplicationSectionsPath = (applicationId, module, sourceId) => {
  const normalizedModule = normalizeApplicationModule(module);
  if (!['brd', 'requirements'].includes(normalizedModule)) return '';
  const sourcePath = `${buildApplicationModulePath(applicationId, normalizedModule)}/${encodeURIComponent(String(sourceId || '').trim())}`;
  return `${sourcePath}/sections?source=history`;
};

export const applicationModeFromPath = (pathname = '') => {
  const route = parseApplicationRoute(pathname);
  if (!route.isApplicationRoute) return '';
  if (!route.module) return route.kind === 'applications' ? 'applications' : 'application';
  return APPLICATION_MODULES[route.module]?.mode || 'application';
};
